import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import BasicTableNoPagination from 'components/layout/tables/BasicTableNoPagination';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getPositionHours, {
  isAverageHourlyEarningsMissing,
} from 'services/offers/getPositionHours';

import 'style/offers/OfferProductPriceSummary.css';
import getContributionMarginPerUnit from 'services/products/calculations/getContributionMarginPerUnit';
import getContributionMarginTotal from 'services/products/calculations/getContributionMarginTotal';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import translations from 'services/translations/translations';

const OfferProductsTable = ({
  products,
  type,
}: {
  products: IOfferProductsGet[];
  type: string;
}): JSX.Element | null => {
  const history = useHistory();

  const { t } = useTranslation([
    translations.common,
    translations.offers,
    translations.products,
  ]);

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories ?? []
  );

  const getContributionMarginPerUnitValue = (
    offerProduct: IOfferProductsGet
  ) => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    if (primaryVersion === undefined) {
      return 0;
    }

    const contributionMarginPerUnit = formatGroupedNumber(
      getContributionMarginPerUnit(
        primaryVersion.installations,
        primaryVersion.subproducts
      )
    );

    const unit = t(`${translations.common}:units:${offerProduct.unit}`);

    return `${contributionMarginPerUnit} €/${unit}`;
  };

  const getContributionMarginRatioValue = (
    offerProduct: IOfferProductsGet
  ): string => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    if (primaryVersion === undefined) {
      return '';
    }

    const contributionMarginRatio = formatGroupedNumber(
      getContributionMarginRatio(
        primaryVersion.installations,
        primaryVersion.subproducts
      )
    );

    return `${contributionMarginRatio} %`;
  };

  const getContributionMarginValue = (
    offerProduct: IOfferProductsGet
  ): string => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    if (primaryVersion === undefined) {
      return '';
    }

    const contributionMargin = formatGroupedNumber(
      getContributionMarginTotal(
        primaryVersion.installations,
        primaryVersion.subproducts,
        offerProduct.quantity
      )
    );

    return `${contributionMargin} €`;
  };

  const getPriceValue = (offerProduct: IOfferProductsGet) => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    if (primaryVersion === undefined) {
      return '';
    }

    const sellingPriceSum = formatGroupedNumber(
      getSellingPriceTotal(
        primaryVersion.installations,
        primaryVersion.subproducts,
        offerProduct.quantity
      )
    );

    return `${sellingPriceSum} €`;
  };

  const getQuantity = (offerProduct: IOfferProductsGet) => {
    const unit = t(`${translations.common}:units:${offerProduct.unit}`);
    return `${formatGroupedNumber(offerProduct.quantity, 2, 0)} ${unit}`;
  };

  const getPricePerUnitValue = (offerProduct: IOfferProductsGet) => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    if (primaryVersion === undefined) {
      return '';
    }

    const sellingPricePerUnit = formatGroupedNumber(
      getSellingPricePerUnit(
        primaryVersion.installations,
        primaryVersion.subproducts
      )
    );

    const unit = t(`${translations.common}:units:${offerProduct.unit}`);

    return `${sellingPricePerUnit} €/${unit}`;
  };

  const getHours = (offerProduct: IOfferProductsGet): number => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    return primaryVersion === undefined
      ? 0
      : getPositionHours(
          primaryVersion.installations,
          primaryVersion.averageHourlyEarnings,
          offerProduct.quantity,
          installationCategories
        );
  };

  const getHoursValue = (offerProduct: IOfferProductsGet): string => {
    const hours = getHours(offerProduct);
    return hours === 0 ? '' : `${formatGroupedNumber(hours)} h`;
  };

  const getContributionMarginPerHourValue = (
    offerProduct: IOfferProductsGet
  ): string => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );
    const hours = getHours(offerProduct);

    if (primaryVersion === undefined || hours === 0) {
      return '';
    }

    const contributionMargin = getContributionMarginTotal(
      primaryVersion.installations,
      primaryVersion.subproducts,
      offerProduct.quantity
    );

    return `${formatGroupedNumber(contributionMargin / hours)} €/h`;
  };

  const isMissingAverageHourlyEarnings = (
    offerProduct: IOfferProductsGet
  ): boolean => {
    const primaryVersion = offerProduct.versions.find(
      (version) => version.isPrimary
    );

    return (
      primaryVersion !== undefined &&
      isAverageHourlyEarningsMissing(
        primaryVersion.installations,
        primaryVersion.averageHourlyEarnings,
        installationCategories
      )
    );
  };

  const displayedProducts = products.map((offerProduct) => ({
    averageHourlyEarningsMissing: isMissingAverageHourlyEarnings(offerProduct),
    contributionMarginPerHour: getContributionMarginPerHourValue(offerProduct),
    hours: getHoursValue(offerProduct),
    contributionMargin: getContributionMarginValue(offerProduct),
    contributionMarginPerUnit: getContributionMarginPerUnitValue(offerProduct),
    contributionMarginRatio: getContributionMarginRatioValue(offerProduct),
    id: offerProduct.id,
    name: offerProduct.name,
    position: offerProduct.position,
    pricePerUnit: getPricePerUnitValue(offerProduct),
    priceTotal: getPriceValue(offerProduct),
    quantity: getQuantity(offerProduct),
  }));

  const themeColorTextClass = 'theme-text-color';

  // Numbers are aligned by their last character so the columns are easy to read
  const numeric = {
    align: 'right',
    headerAlign: 'right',
    style: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  };

  const columns = [
    {
      dataField: 'position',
      text: t(`${translations.products}:Position`),
      headerStyle: () => {
        return { width: '12%' };
      },
    },
    {
      dataField: 'name',
      text: t(`${translations.products}:Product`),
      headerStyle: () => {
        return { width: '35%' };
      },
    },
    {
      ...numeric,
      dataField: 'quantity',
      text: t(`${translations.common}:Quantity`),
    },
    {
      ...numeric,
      classes: themeColorTextClass,
      dataField: 'pricePerUnit',
      headerClasses: themeColorTextClass,
      text: t(`${translations.offers}:Price per unit`),
    },
    {
      ...numeric,
      classes: themeColorTextClass,
      dataField: 'contributionMarginPerUnit',
      headerClasses: themeColorTextClass,
      text: t(`${translations.offers}:Contribution margin per unit €`),
    },
    {
      ...numeric,
      dataField: 'contributionMarginRatio',
      text: `${t(`${translations.offers}:Contribution margin`)} %`,
    },
    {
      ...numeric,
      dataField: 'contributionMargin',
      text: `${t(`${translations.offers}:Contribution margin`)} €`,
    },
    {
      ...numeric,
      dataField: 'hours',
      text: t(`${translations.offers}:Hours`),
      // Installation work without average hourly earnings has no hours
      formatter: (
        cell: string,
        row: { averageHourlyEarningsMissing: boolean }
      ) =>
        row.averageHourlyEarningsMissing ? (
          <span
            className="missing-average-hourly-earnings"
            title={t(
              `${translations.offers}:Average hourly earnings missing info`
            )}
          >
            {t(`${translations.offers}:Average hourly earnings missing`)}
          </span>
        ) : (
          cell
        ),
    },
    {
      ...numeric,
      dataField: 'contributionMarginPerHour',
      text: t(`${translations.offers}:Contribution margin per hour`),
    },
    {
      ...numeric,
      dataField: 'priceTotal',
      text: t(`${translations.common}:Total`),
    },
  ];

  const setSelectedRow = (index: number) => {
    history.push(`/offers/products/${displayedProducts[index].id}/${type}`);
  };

  return (
    <BasicTableNoPagination
      columns={columns}
      data={displayedProducts}
      keyField="id"
      setSelectedRow={setSelectedRow}
    />
  );
};

export default OfferProductsTable;
